import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router";
import {
  Stack,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  useTheme,
  IconButton,
  Chip,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import ViewEditTabContent from "./ViewEditTabContent";
import AICopilotChat from "../AICopilot/AICopilotChat";
import DonutChart from "./DonutChart";
import { useQuery } from "@apollo/client";
import { GET_CAREPLAN_TEMPLATE } from "../../graphql/TemplateQueries";

interface BlueprintLayoutProps {
  children?: React.ReactNode;
}

interface NavigationStep {
  id: string;
  label: string;
  path: string;
}

const BlueprintLayout = ({ children }: BlueprintLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { recipeName, templateId } = useParams<{
    recipeName: string;
    templateId: string;
  }>();
  const theme = useTheme();

  // Fetch template data
  const { data: templateData } = useQuery(GET_CAREPLAN_TEMPLATE, {
    variables: { templateId },
    skip: !templateId,
  });

  const templateLabel = templateData?.template?.name || "Loading...";

  const [selectedRecipe, setSelectedRecipe] = useState<string>(
    decodeURIComponent(recipeName || "ProstateNodes.toml"),
  );

  // Get blueprint metadata for the current recipe
  const getBlueprintData = (recipeName: string) => {
    // Mock data matching BlueprintDashboard structure
    const blueprintData: {
      [key: string]: { diseaseGroup: string; successRate: number };
    } = {
      "ProstateNodes.toml": { diseaseGroup: "Prostate", successRate: 48 },
      "BreastIMRT_Standard.toml": { diseaseGroup: "Breast", successRate: 73.5 },
      "LungSBRT_Central.toml": { diseaseGroup: "Lung", successRate: 74.8 },
      "HeadNeckIMRT_Complex.toml": {
        diseaseGroup: "Head & Neck",
        successRate: 75.2,
      },
      "RectumVMAT_HighRisk.toml": { diseaseGroup: "Rectum", successRate: 76.1 },
      "CervixIMRT_Advanced.toml": { diseaseGroup: "Cervix", successRate: 77.3 },
      "PancreasSBRT_Borderline.toml": {
        diseaseGroup: "Pancreas",
        successRate: 78.6,
      },
      "EsophagusCRT_Concurrent.toml": {
        diseaseGroup: "Esophagus",
        successRate: 79.2,
      },
      "LiverSBRT_Metastases.toml": { diseaseGroup: "Liver", successRate: 79.4 },
      "BrainSRS_Multiple.toml": { diseaseGroup: "Brain", successRate: 79.8 },
    };

    return (
      blueprintData[recipeName] || { diseaseGroup: "Unknown", successRate: 0 }
    );
  };

  const currentBlueprintData = getBlueprintData(selectedRecipe);
  const [useDiffEditor, setUseDiffEditor] = useState<boolean>(false);
  const [showAICopilot, setShowAICopilot] = useState<boolean>(true);
  const [editorTheme, setEditorTheme] = useState<"vs-light" | "vs-dark">(
    "vs-dark",
  );
  const [modifiedRecipeContent, setModifiedRecipeContent] = useState<
    string | null
  >(null);

  const navigationSteps: NavigationStep[] = [
    {
      id: "view-edit",
      label: "View & Edit",
      path: `/blueprint/${encodeURIComponent(selectedRecipe)}`,
    },
    {
      id: "plan-generation",
      label: "Plan Generation",
      path: `/blueprint/${encodeURIComponent(selectedRecipe)}/generate`,
    },
    {
      id: "plan-tracking",
      label: "Plan Tracking",
      path: `/blueprint/${encodeURIComponent(selectedRecipe)}/tracking`,
    },
    {
      id: "plan-comparison",
      label: "Plan Comparison",
      path: `/blueprint/${encodeURIComponent(selectedRecipe)}/compare`,
    },
  ];

  // Determine active step based on current route
  const getActiveStep = () => {
    if (location.pathname.includes("/generate")) {
      return "plan-generation";
    } else if (location.pathname.includes("/tracking")) {
      return "plan-tracking";
    } else if (location.pathname.includes("/compare")) {
      return "plan-comparison";
    }
    return "view-edit";
  };

  const [activeStep, setActiveStep] = useState(getActiveStep());

  // Sync activeStep with route changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setActiveStep(getActiveStep());
  }, [location.pathname]);

  const handleStepClick = (step: NavigationStep) => {
    setActiveStep(step.id);
    navigate(step.path);
  };

  const handleRecipeModified = (recipeName: string, content: string) => {
    // Handle recipe modifications from AI assistant
    if (recipeName === selectedRecipe) {
      setModifiedRecipeContent(content);
      // Automatically switch to diff editor when AI assistant makes edits
      setUseDiffEditor(true);
    }
  };

  const isViewEditStep = activeStep === "view-edit";
  // Hide AI Copilot on non-view-edit steps or when showAICopilot is false
  const shouldShowAICopilot = showAICopilot && isViewEditStep;

  // Calculate width based on step and AI Copilot visibility
  const getCentralContentWidth = () => {
    if (isViewEditStep) {
      // On View & Edit step, expand if AI Copilot is hidden
      return shouldShowAICopilot ? "60%" : "90%";
    } else {
      // On other steps, always use 60% to leave space for where AI Copilot would be
      return "60%";
    }
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      height={1}
      sx={{ backgroundColor: "background.default" }}
    >
      {/* Left Navigation Sidebar */}
      <Stack
        width="240px"
        minWidth={"240px"}
        spacing={2}
        sx={{
          backgroundColor: "background.secondary",
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Back to Dashboard Button */}
        <Stack
          direction="row"
          alignItems="center"
          padding={1}
          borderBottom={1}
          borderColor={"divider"}
          gap={1}
          onClick={() => {
            navigate("/blueprint");
          }}
          sx={{ cursor: "pointer" }}
          height={70}
        >
          <IconButton>
            <ArrowBack />
          </IconButton>
          <Stack>
            <Typography variant="body1">Back</Typography>
            <Typography color={"text.secondary"} variant="body1">
              Blueprint Dashboard
            </Typography>
          </Stack>
        </Stack>

        {/* Template Name */}
        {templateId && (
          <Stack
            direction="column"
            alignItems="flex-start"
            padding={2}
            borderBottom={1}
            borderColor={"divider"}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              Template
            </Typography>
            <Typography
              variant="body1"
              title={templateData?.template?.name ?? ""}
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {templateLabel}
            </Typography>
          </Stack>
        )}

        {/* Navigation Steps */}
        <List sx={{ padding: 0 }}>
          {navigationSteps.map((step) => (
            <ListItem
              key={step.id}
              onClick={() => handleStepClick(step)}
              sx={{
                cursor: "pointer",
                borderRadius: 1,
                backgroundColor:
                  activeStep === step.id
                    ? theme.palette.secondary.light
                    : "transparent",
                "&:hover": {
                  backgroundColor:
                    activeStep === step.id
                      ? theme.palette.secondary.light
                      : "#f5f5f5",
                },
                borderLeft: "4px solid",
                borderColor:
                  activeStep === step.id
                    ? theme.palette.primary.main
                    : "transparent",
                paddingLeft: 1,
              }}
            >
              <ListItemText primary={step.label} />
            </ListItem>
          ))}
        </List>
      </Stack>

      {/* Central Content */}
      <Stack width={getCentralContentWidth()} spacing={2}>
        {/* Content Area */}
        <Stack height={1} padding={2} gap={2}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="h6">
              {selectedRecipe.replace(".toml", "")}
            </Typography>
            <Chip
              label={currentBlueprintData.diseaseGroup}
              variant="outlined"
              size="small"
              color="primary"
            />
            <Chip
              label={`${currentBlueprintData.successRate}% Success Rate`}
              variant="filled"
              size="small"
              color={
                currentBlueprintData.successRate >= 75
                  ? "success"
                  : currentBlueprintData.successRate >= 50
                    ? "warning"
                    : "error"
              }
            />
          </Stack>

          {isViewEditStep ? (
            // View & Edit content
            <ViewEditTabContent
              selectedRecipe={selectedRecipe}
              useDiffEditor={useDiffEditor}
              setUseDiffEditor={setUseDiffEditor}
              editorTheme={editorTheme}
              setEditorTheme={setEditorTheme}
              showAICopilot={showAICopilot}
              setShowAICopilot={setShowAICopilot}
              modifiedRecipeContent={modifiedRecipeContent}
            />
          ) : (
            // Other step content
            <Box sx={{ height: "100%" }}>{children}</Box>
          )}
        </Stack>
      </Stack>

      {shouldShowAICopilot && (
        <Stack
          width="30%"
          spacing={2}
          sx={{
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={2}>
            {/* Donut Chart */}
            <DonutChart
              size={200}
              title="Plan Rejection Reasons"
              data={[
                {
                  label: "Rectum maximum dose too high",
                  value: 62,
                  color: "#a78bfa",
                },
                { label: "PTV coverage too low", value: 14, color: "#06b6d4" },
                {
                  label: "Rectum dose out of tolerance",
                  value: 11,
                  color: "#fb923c",
                },
                {
                  label: "Maximum point outside CTV_High",
                  value: 8,
                  color: "#f87171",
                },
                {
                  label: "Bowel dose out of tolerance",
                  value: 5,
                  color: "#9ca3af",
                },
              ]}
            />
            {/* Date Range Header */}
            <Box sx={{ padding: 2, paddingBottom: 0 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                Data from last 4 weeks
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                align="center"
                display="block"
              >
                {(() => {
                  const endDate = new Date();
                  const startDate = new Date();
                  startDate.setDate(endDate.getDate() - 28);
                  return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
                })()}
              </Typography>
            </Box>
          </Stack>

          {/* AI Copilot */}
          <AICopilotChat
            selectedRecipe={selectedRecipe}
            onRecipeModified={handleRecipeModified}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default BlueprintLayout;
